import { DeleteOutlined, InfoCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, InputNumber, Select, Tooltip } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useContext, useId, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  RepeatableColumn,
  SelectOption,
  WorkflowDateFormat,
  WorkflowField,
} from '@/api/workflows';
import { BmoDatePicker } from '@/components/BmoDatePicker';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';
import { ICON_BTN_PROPS } from '@/components/iconButton';

import { columnCellMessage, columnCellViolation } from './columnCellRules';
import { DateFormatContext } from './dateFormat';
import { clearOnEmptyBackspace } from './dateInputClear';
import { useEmailVerify } from './emailVerifyContext';
import type { FieldValue, RepeatableRowValue } from './FieldRenderer';
import { parseTypedDate, validateTypedDate } from './fieldValidation';
import { FreshEntryErrorScope } from './FreshEntryErrorScope';
import { effectiveMaxLength } from './lengthRule';
import {
  integerDigitCap,
  numericInputGuard,
  numericParser,
  thousandsFormatter,
} from './numericInput';
import {
  computeRepeatableSum,
  hasRepeatableColumnData,
  readUnitMode,
  resolveColumnLabel,
  resolveSumTarget,
  resolveUnitModeTarget,
  unitModeKey,
  unitSymbol,
  type UnitMode,
} from './repeatableSum';
import { RequiredMark } from './RequiredMark';
import { ShowErrorsContext } from './showErrorsContext';
import { resolveValidationMessage } from './validationMessage';

/**
 * Runtime control for a `RepeatableRow` field. Renders one row per entry in
 * `value` using the field's `repeatableTemplate` as the column spec; appends
 * a "+ Add Another Row" button at the bottom and a per-row red trash icon
 * for removal. Layout matches the dashed-border container the BMO Carlyle
 * smartform's Step 5 mockups use.
 *
 * Designed to compose with regular conditional ShortText / Number fields
 * around it — if a particular form needs a "header row" above the repeatable
 * list (e.g. clause (w)'s "Individual who established trust" pair), the
 * originator drops two ShortTexts above the RepeatableRow and shares the
 * same visibility condition. No nested-header primitive is needed.
 */
export function RepeatableRowControl({
  field,
  value,
  onChange,
  language,
  getValueById,
  setValueById,
  disabled,
}: {
  field: WorkflowField;
  value: RepeatableRowValue[];
  onChange: (next: RepeatableRowValue[]) => void;
  language: 'en' | 'fr';
  /**
   * Renders every cell, both unit toggles, and the Add / Remove buttons inert.
   * Merged by FieldRenderer from the field's own `readOnly` flag, its "Disable if"
   * rule, and the shell lock.
   *
   * This control carried NO such prop until the "Disable if" work: a locked
   * application left its rows fully editable, and only FormShell's dead `onChange`
   * stopped the writes landing. That fallback does not exist for a rule-disabled
   * field — the rest of the step stays live — so the affordance has to be real here.
   */
  disabled?: boolean;
  /** Cross-field value lookup — supplied by FieldRenderer for RepeatableRow
   *  fields carrying a `sumValidation` that targets another field's value, and
   *  for reading a dual-unit column's current mode. */
  getValueById?: (id: string) => FieldValue;
  /** Persists a dual-unit column's chosen mode. Keyed by COLUMN, not row —
   *  the toggle governs the whole column, matching live RIF where it is drawn
   *  once in the column header. */
  setValueById?: (id: string, next: FieldValue) => void;
}) {
  const { t } = useTranslation();
  const template = field.repeatableTemplate ?? [];

  // Empty rows render as a single editable placeholder row so the advisor
  // sees something to fill in immediately — matches the mockups.
  const rows: RepeatableRowValue[] = value.length === 0 ? [emptyRow(template)] : value;

  // Column widths come from each column's authored width percentage (1–100),
  // applied as a literal `%` track so the value is taken at face value: columns
  // whose percentages sum to under 100 leave blank space on the right rather
  // than stretching to fill. A column with no percentage set falls back to an
  // equal 1fr share, so an all-unset template still renders equal columns.
  //
  // The gaps have to come OUT of those percentages. A grid of `20% 15% …`
  // summing to 100 plus `gap: 12` is 100% + (n-1)×12px wide — it overflows its
  // container and clips the last column. RIF's six-column fund table hit this:
  // five gaps put 60px past the right edge and Payment was cut in half.
  //
  // Subtracting an equal share of the total gap from every track makes the sum
  // exact again — 100% − 60px of tracks + 60px of gaps — while preserving the
  // face-value semantics above. minmax(0, …) stops a wide input from pushing
  // its track past the computed width.
  // Each dual-unit column's CURRENT mode, resolved once per render so the
  // header control, the cell inputs and the totals below all agree.
  const unitModes: Record<string, UnitMode> = {};
  for (const col of template) {
    if (col.unitToggle) unitModes[col.id] = readUnitMode(field, col, getValueById);
  }

  // The delete button sits in a 40px gutter to the right of the grid. It has
  // to be reserved on EVERY row and on the header, not just the rows that
  // happen to show a button — otherwise an anchored first row (no button) is
  // 40px wider than the rows below it and the columns visibly disagree.
  const removeGutterPx = 40;
  // True when ANY row can be removed. Both variants of the per-row rule below
  // reduce to the same condition — anchored makes rows 1+ removable, unanchored
  // makes any row removable while one survives — so a single row never has a
  // gutter and two or more always do.
  const showRemoveGutter = rows.length > 1;

  const gapPx = 12;
  const gapShare = template.length > 1 ? (gapPx * (template.length - 1)) / template.length : 0;
  const gridTemplateColumns = template
    .map((c) =>
      c.widthPercent && c.widthPercent > 0
        ? `minmax(0, calc(${c.widthPercent}% - ${gapShare}px))`
        : 'minmax(0, 1fr)',
    )
    .join(' ');

  // Card layout (displayAs 'card', the 2026-07-24 BMO "Funds" design): each
  // entry becomes a bordered block with its labels inside it and the columns
  // WRAPPING into a two-column grid, instead of one wide table line under a
  // shared header. Presentation only — template, row cap and the cross-row
  // sum rule are untouched, which is why the design could stay on
  // RepeatableRow rather than moving to a repeatable section (which has no
  // cross-instance sum).
  const cardLayout = field.displayAs === 'card';
  // In card layout `widthPercent` picks how many of the two tracks a column
  // takes: >= 100 spans the full width (Fund name), anything else takes one
  // (the 50/50 pairs). Unset behaves as a half so an unconfigured template
  // still lays out sensibly rather than collapsing to one column.
  const cardSpan = (c: RepeatableColumn) => ((c.widthPercent ?? 50) >= 100 ? 2 : 1);

  // A shared header row surfaces each column's name + help bubble once above the
  // data rows. Card layout labels each cell individually instead.
  const showHeader =
    !cardLayout &&
    template.some((c) => resolveColumnLabel(c, language, unitModes[c.id]).length > 0);

  // A table of THREE or more columns is unreadable at phone width — the TFSA
  // beneficiary table puts first name / last name / relationship on one 375px
  // line — so on a narrow canvas it reflows to two columns per line.
  //
  // One and two column tables are left alone: two already fit, and forcing a
  // single column into a two-track grid would halve its input for no reason.
  // Whether the reflow has actually FIRED is a CSS question (see global.css);
  // this flag only decides whether the markup it needs is present at all, so a
  // table that can never reflow carries none of it.
  const canReflowNarrow = !cardLayout && template.length > 2;
  // Each cell names itself in card layout, and in a reflowed table — where the
  // shared header has to go, because its labels and the inputs they name sit in
  // separate grids: wrap a third column and its label lands above the NEXT
  // row's first input rather than its own.
  const labelCells = cardLayout || canReflowNarrow;

  const updateCell = (rowIdx: number, colId: string, next: string | number | boolean | null) => {
    const draft = rows.map((r, i) => (i === rowIdx ? { ...r, [colId]: next } : r));
    onChange(draft);
  };

  const addRow = () => onChange([...rows, emptyRow(template)]);

  const removeRow = (idx: number) => {
    const next = rows.filter((_, i) => i !== idx);
    // If the advisor removes the last row, fall back to a single empty
    // placeholder so the dashed container always has something visible.
    onChange(next.length === 0 ? [] : next);
  };

  // Hide the "+ Add Another Row" button once the originator-authored cap
  // is reached. Null = unlimited.
  const maxRows = field.maxRows ?? null;
  const canAddRow = maxRows === null || rows.length < maxRows;
  // Button copy names the thing being added and states the cap, per the
  // 2026-07-24 BMO design ("ADD ANOTHER FUND (MAXIMUM 5)"). The noun comes
  // from the field's own label so the originator controls it; falls back to
  // the generic "Add Another Row" when the field is unlabelled — or when the
  // originator hid the label, since naming a noun the advisor cannot see
  // anywhere on the page reads as a stray word.
  const addRowNoun = field.hideLabel
    ? undefined
    : (language === 'fr' && field.labelFr ? field.labelFr : field.labelEn)?.trim();
  const addRowLabel = addRowNoun
    ? maxRows !== null
      ? t('formRenderer.repeatable.addRowNamedCapped', { name: addRowNoun, max: maxRows })
      : t('formRenderer.repeatable.addRowNamed', { name: addRowNoun })
    : t('formRenderer.repeatable.addRow');

  // Cross-row sum summary — only rendered when the field carries a
  // sumValidation AND the target is currently resolvable. Rendered live
  // so the advisor sees the delta while editing.
  const sv = field.sumValidation ?? null;
  const currentSum = sv ? computeRepeatableSum(field, rows) : null;
  const targetSum = sv ? resolveSumTarget(sv, getValueById) : null;
  // Originator-authored caption for the total, shown in place of the generic
  // "Current sum" wording. Prefer the active language, fall back to the other
  // so a single-language caption still shows rather than reverting to generic.
  const sumLabel = sv
    ? (language === 'fr' ? sv.labelFr || sv.labelEn : sv.labelEn || sv.labelFr) || null
    : null;
  // Held back until the summed column actually carries a figure. An untouched
  // table and a table of zeroes both total 0, so without this the advisor opens
  // every preview to a red "Total: 0 / Target: 100%" line reporting a shortfall
  // they have not had the chance to cause. Display only — FormShell's
  // `isSumSatisfied` gate is unchanged, so an empty table still blocks Next.
  const showSummary = sv !== null && currentSum !== null && hasRepeatableColumnData(field, rows);
  const summaryOff = showSummary && targetSum !== null && Math.abs(currentSum - targetSum) >= 0.005;
  // The field's own wording for a total that misses its target (QA INV-015 / INV-017: "Total
  // value of all the funds should be 100%."), shown above the figures. No generic sentence —
  // the figures already say it where nothing is authored.
  const sumMessage =
    summaryOff && field.validationMessages?.some((m) => m.key === 'sum')
      ? resolveValidationMessage(
          field,
          'sum',
          {
            target: formatSumValue(targetSum!, language),
            current: formatSumValue(currentSum!, language),
          },
          language,
          t,
        )
      : null;

  // Per-mode running totals for dual-unit columns. Only shown when the ACTIVE
  // mode carries a target — RIF leaves dollar mode unconstrained, and showing
  // "Total: $0 / target —" there would be noise.
  const unitTotals = template.flatMap((col) => {
    const toggle = col.unitToggle;
    if (!toggle) return [];
    // unitModes is populated for every column carrying a toggle, but read it
    // defensively so a template change can't produce an undefined mode.
    const mode = unitModes[col.id] ?? readUnitMode(field, col, getValueById);
    const target = resolveUnitModeTarget(toggle, mode, getValueById);
    if (target === null) return [];
    // Same hold-back as `showSummary` below, per column: no figures (and no
    // off-target sentence) until this column has a number in it.
    if (!hasRepeatableColumnData(field, rows, col.id)) return [];
    const total = computeRepeatableSum(field, rows, col.id) ?? 0;
    return [
      {
        id: col.id,
        label: resolveColumnLabel(col, language, mode),
        mode,
        total,
        target,
        off: Math.abs(total - target) >= 0.005,
      },
    ];
  });

  return (
    <div
      style={{
        // Cards carry their own borders, so the outer dashed/tinted frame
        // (which reads as a table container) is dropped in that mode.
        border: cardLayout ? undefined : '1px dashed #93c5fd',
        borderRadius: cardLayout ? undefined : 8,
        padding: cardLayout ? 0 : 16,
        background: cardLayout ? undefined : '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Card layout has no header row, so a dual-unit column's $ / % choice sits above the cards —
          as RDSP's "Funds type" radio ($ value / % value). Same mode key as the header checkbox. */}
      {cardLayout &&
        template
          .filter((col) => col.unitToggle)
          .map((col) => {
            const label = resolveColumnLabel(col, language, unitModes[col.id]);
            // An authored toggle label names the control outright ("Funds type"); with none,
            // the column's own label names it ("<column> type"), as it always did.
            const toggleLabel =
              (language === 'fr' ? col.unitToggle?.labelFr : col.unitToggle?.labelEn) ||
              t('formRenderer.repeatable.unitChoice', { column: label });
            return (
              <div
                key={`unit-${col.id}`}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <span id={`unit-${field.id}-${col.id}`} style={{ fontSize: 14, color: '#1f2937' }}>
                  {toggleLabel}
                </span>
                <BmoRadioGroup<'dollar' | 'percent'>
                  ariaLabelledBy={`unit-${field.id}-${col.id}`}
                  value={unitModes[col.id] ?? 'dollar'}
                  disabled={disabled || !setValueById}
                  onChange={(next) => setValueById?.(unitModeKey(field.id, col.id), next)}
                  options={[
                    { value: 'dollar', label: t('formRenderer.repeatable.unitDollar') },
                    { value: 'percent', label: t('formRenderer.repeatable.unitPercent') },
                  ]}
                />
              </div>
            );
          })}
      {showHeader && (
        <div
          // Dropped on a narrow canvas, where each cell carries its own label
          // instead — see the cell label block below. Unclassed on a table that
          // cannot reflow, so its header survives every width.
          className={canReflowNarrow ? 'bmo-repeatable-head' : undefined}
          style={{
            display: 'grid',
            gridTemplateColumns,
            gap: gapPx,
            paddingRight: showRemoveGutter ? removeGutterPx : 0,
          }}
        >
          {template.map((col) => {
            const mode = unitModes[col.id];
            const label = resolveColumnLabel(col, language, mode);
            const help = columnHelp(col, language);
            const toggle = col.unitToggle ?? null;
            return (
              <div
                key={col.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#334155',
                }}
              >
                <span>
                  {label}
                  {col.required && <RequiredMark />}
                </span>
                {/* Dual-unit control: ONE checkbox per column, in the header,
                    flipping every row at once. Live RIF renders it exactly
                    here, labelled "$/%". */}
                {toggle && (
                  <Checkbox
                    checked={mode === 'percent'}
                    disabled={disabled || !setValueById}
                    onChange={(e) =>
                      setValueById?.(
                        unitModeKey(field.id, col.id),
                        e.target.checked ? 'percent' : 'dollar',
                      )
                    }
                    aria-label={t('formRenderer.repeatable.unitToggleAria', {
                      column: label,
                      defaultValue: `Switch ${label} between dollars and percent`,
                    })}
                    style={{ marginLeft: 4, fontWeight: 400 }}
                  >
                    <span style={{ fontSize: 12, color: '#475569' }}>
                      {(language === 'fr' ? toggle.labelFr : toggle.labelEn) || '$/%'}
                    </span>
                  </Checkbox>
                )}
                {help && (
                  <Tooltip title={help}>
                    <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 12 }} />
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
      )}
      {rows.map((row, rowIdx) => {
        // Anchoring is now an AUTHORED property, not a side effect of layout.
        // It had been implied by card layout alone, which left "table chrome
        // with a locked first row" — live RIF's fund table — unexpressible.
        //
        // Unset falls back to the historical default so nothing already built
        // changes: cards anchor row 0, tables allow removing any row while one
        // survives.
        const anchored = field.anchorFirstRow ?? cardLayout;
        const removable = anchored ? rowIdx > 0 : rows.length > 1;
        const removeButton = (
          <Button
            {...ICON_BTN_PROPS}
            danger
            disabled={disabled}
            icon={<DeleteOutlined />}
            onClick={() => removeRow(rowIdx)}
            aria-label={t('formRenderer.repeatable.removeRow')}
            style={
              cardLayout
                ? ICON_BTN_PROPS.style
                : { ...ICON_BTN_PROPS.style, position: 'absolute', top: 4, right: 0 }
            }
          />
        );
        // Next holds a row that must be complete — every row of a required table, and any row the
        // advisor started (FormShell's incompleteRepeatableColumns) — so its blank required cells
        // say so once Next is pressed (QA DP-018: the PAC fund row stayed silent).
        const rowMustBeComplete =
          !!field.required ||
          template.some((c) => isCellFilled(row[c.id])) ||
          (!!field.checkBlankRows && rowIdx > 0);
        const cells = template.map((col) => {
          const cellLabel = resolveColumnLabel(col, language, unitModes[col.id]);
          const cellHelp = columnHelp(col, language);
          // Blank and required. Drives BOTH the message under the cell and the
          // cell's own red border — the message used to be a sibling the
          // control knew nothing about, so the input stayed grey while the text
          // beneath it said "Required field."
          //
          // The "has Next been pressed" half is NOT applied here: these cells
          // render inside this row's FreshEntryErrorScope, which hands a row
          // added after a refused Next its own tick of 0. Reading the tick out
          // here would read the step-wide one and paint a brand-new row red.
          // Both consumers below gate it through `useRequiredCellError`.
          const requiredBlank = !!col.required && rowMustBeComplete && !isCellFilled(row[col.id]);
          return (
            <div
              key={col.id}
              // A column too wide to share a line keeps the reflowed line to
              // itself — the same call `cardSpan` already makes for a card.
              className={
                canReflowNarrow && cardSpan(col) === 2 ? 'bmo-repeatable-cell-full' : undefined
              }
              style={cardLayout ? { gridColumn: `span ${cardSpan(col)}` } : undefined}
            >
              {/* In a table this copy is the HEADER's understudy: both are in the
                  markup and CSS shows exactly one, the header at desktop width
                  and these at phone width. See `.bmo-repeatable-cell-label--table`
                  in global.css. */}
              {labelCells && cellLabel.length > 0 && (
                <div
                  className={`bmo-repeatable-cell-label${cardLayout ? '' : ' bmo-repeatable-cell-label--table'}`}
                >
                  <span>
                    {cellLabel}
                    {col.required && <RequiredMark />}
                  </span>
                  {cellHelp && (
                    <Tooltip title={cellHelp}>
                      <InfoCircleOutlined style={{ color: '#0179C1', fontSize: 12 }} />
                    </Tooltip>
                  )}
                </div>
              )}
              <RepeatableCellInput
                unitMode={unitModes[col.id]}
                col={col}
                value={row[col.id] ?? null}
                onChange={(next) => updateCell(rowIdx, col.id, next)}
                language={language}
                // The card labels every cell, so a placeholder repeating the
                // label would just be noise under it.
                hidePlaceholder={cardLayout}
                requiredBlank={requiredBlank}
                disabled={disabled}
              />
              <RequiredCellError col={col} language={language} requiredBlank={requiredBlank} />
            </div>
          );
        });

        // Card: the remove button sits on the same band as the inputs, to the
        // right of the last cell. `alignItems: flex-end` lines it up with the
        // input rather than the cell label above it, and survives a label
        // wrapping to two lines. It previously had its own strip above the
        // grid, which left an empty band across the top of every card.
        // Absolutely positioning it (as the table does) is not an option —
        // that drops it on top of the first full-width input.
        if (cardLayout) {
          return (
            // A row added after a refused Next starts without red — see FreshEntryErrorScope.
            <FreshEntryErrorScope key={rowIdx}>
              <div
                className="bmo-repeatable-card"
                style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}
              >
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 16,
                  }}
                >
                  {cells}
                </div>
                {removable && <div style={{ paddingBottom: 4 }}>{removeButton}</div>}
              </div>
            </FreshEntryErrorScope>
          );
        }

        return (
          <FreshEntryErrorScope key={rowIdx}>
            <div
              // Re-templated to two columns on a narrow canvas — the authored
              // percentages below are a desktop-width instruction.
              className={canReflowNarrow ? 'bmo-repeatable-grid' : undefined}
              style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns,
                gap: gapPx,
                // Reserved on every row, so an un-removable row lines up with a
                // removable one.
                paddingRight: showRemoveGutter ? removeGutterPx : 0,
              }}
            >
              {cells}
              {removable && removeButton}
            </div>
          </FreshEntryErrorScope>
        );
      })}
      {canAddRow && (
        <Button
          type="link"
          disabled={disabled}
          icon={<PlusCircleOutlined style={{ fontSize: 18 }} />}
          onClick={addRow}
          // Same BMO treatment as the repeatable-SECTION add button (see
          // FormRenderer) so the two "add another" affordances read alike —
          // including `.bmo-add-another`, which lets an originator-authored
          // noun wrap to a second line instead of running off a phone's edge.
          className="bmo-add-another"
          style={{
            alignSelf: 'flex-start',
            paddingLeft: 0,
            height: 'auto',
            color: '#0079C1',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {addRowLabel}
        </Button>
      )}
      {unitTotals.map((u) => (
        <Fragment key={`total-${u.id}`}>
          {/* Opt-in (RESP QA TC-10-09): the field's own sentence for a $ total that misses its target. */}
          {u.off &&
            u.mode === 'dollar' &&
            field.validationMessages?.some((m) => m.key === 'sum.amount') && (
              <div
                role="alert"
                style={{ fontSize: 13, fontWeight: 500, color: '#b91c1c', textAlign: 'right' }}
              >
                {resolveValidationMessage(field, 'sum.amount', undefined, language, t)}
              </div>
            )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              fontSize: 13,
              fontWeight: 500,
              color: u.off ? '#b91c1c' : '#059669',
              paddingTop: 4,
              borderTop: '1px solid #e5e7eb',
            }}
            role={u.off ? 'alert' : undefined}
          >
            <span>
              {t('formRenderer.repeatable.sumCurrentLabeled', {
                label: u.label,
                value:
                  u.mode === 'percent'
                    ? `${formatSumValue(u.total, language)}%`
                    : formatSumValue(u.total, language),
              })}
            </span>
            <span>
              {t('formRenderer.repeatable.sumTarget', {
                value:
                  u.mode === 'percent'
                    ? `${formatSumValue(u.target, language)}%`
                    : formatSumValue(u.target, language),
              })}
            </span>
          </div>
        </Fragment>
      ))}
      {sumMessage && (
        <div
          role="alert"
          style={{ fontSize: 13, fontWeight: 500, color: '#b91c1c', textAlign: 'right' }}
        >
          {sumMessage}
        </div>
      )}
      {showSummary && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            fontSize: 13,
            fontWeight: 500,
            color: summaryOff ? '#b91c1c' : '#059669',
            paddingTop: 4,
            borderTop: '1px solid #e5e7eb',
          }}
          role={summaryOff && !sumMessage ? 'alert' : undefined}
        >
          <span>
            {sumLabel
              ? t('formRenderer.repeatable.sumCurrentLabeled', {
                  label: sumLabel,
                  value: formatSumValue(currentSum!, language),
                })
              : t('formRenderer.repeatable.sumCurrent', {
                  value: formatSumValue(currentSum!, language),
                })}
          </span>
          {targetSum !== null && (
            <span>
              {t('formRenderer.repeatable.sumTarget', {
                value: formatSumValue(targetSum, language),
              })}
            </span>
          )}
          {summaryOff && targetSum !== null && (
            <span>
              {t('formRenderer.repeatable.sumDelta', {
                value: formatSumValue(currentSum! - targetSum, language),
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** In the advisor's language — French groups thousands with a space ("1 000", QA N3), not "1,000". */
function formatSumValue(v: number, language: 'en' | 'fr'): string {
  return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(v);
}

function RepeatableCellInput({
  col,
  value,
  onChange,
  language,
  hidePlaceholder,
  unitMode,
  requiredBlank,
  disabled,
}: {
  col: RepeatableColumn;
  value: string | number | boolean | null;
  onChange: (next: string | number | boolean | null) => void;
  language: 'en' | 'fr';
  /** See {@link RepeatableRowControl}'s prop of the same name. Every branch below
   *  must forward it — a cell that ignores it stays typeable inside a locked form. */
  disabled?: boolean;
  /** Card layout renders a real label above each cell, so the label-derived
   *  placeholder underneath it would be redundant. An originator-authored
   *  placeholder is still honoured. */
  hidePlaceholder?: boolean;
  /** Set on a dual-unit column — drives the affix and the 0–100 bound. */
  unitMode?: UnitMode;
  /** The cell is blank and required — see the caller. Gated on Next below, then
   *  paints the red border the sibling message alone could not. Text and date
   *  cells OR it with the column-rule violation they detect themselves. */
  requiredBlank?: boolean;
}) {
  const { t } = useTranslation();
  const invalid = useRequiredCellError(!!requiredBlank);
  // The workflow's date format (QA D-01), same as a Date field's — see DateFormatContext.
  const dateFormat = useContext(DateFormatContext);
  const label = resolveColumnLabel(col, language, unitMode);
  // A dual-unit column's label carries its unit, the way live RIF shows
  // "Amount $" / "Amount %". The authored label stays unit-free so the
  // originator writes it once.
  //
  // Unless it DOESN'T: a `{type}` token means the author placed the symbol
  // themselves, mid-label ("Valeur en {type}") where a suffix cannot reach.
  // Appending as well would print it twice — "$ Value $".
  const authorPlacedUnit =
    /\{type\}/i.test(col.labelEn ?? '') || /\{type\}/i.test(col.labelFr ?? '');
  const unitSuffix = unitMode && !authorPlacedUnit ? ` ${unitSymbol(unitMode)}` : '';
  const labelWithRequired = `${label}${unitSuffix}${col.required ? ' *' : ''}`;
  const placeholder = col.placeholder ?? (hidePlaceholder ? undefined : labelWithRequired);

  if (col.type === 'Number') {
    return (
      <NumberCell
        col={col}
        value={typeof value === 'number' ? value : null}
        onChange={onChange}
        placeholder={placeholder}
        unitMode={unitMode}
        language={language}
        requiredMissing={invalid}
        disabled={disabled}
      />
    );
  }
  if (col.type === 'Date') {
    return (
      <DateCell
        col={col}
        value={typeof value === 'string' ? value : null}
        onChange={onChange}
        placeholder={placeholder}
        format={dateFormat}
        language={language}
        requiredMissing={invalid}
        disabled={disabled}
      />
    );
  }
  if (col.type === 'SingleSelect') {
    return (
      <Select
        value={typeof value === 'string' ? value : undefined}
        onChange={(v) => onChange(v ?? null)}
        disabled={disabled}
        // A card labels the cell above it, so the hint is the stories' "Select" (QA D8).
        placeholder={placeholder ?? t('formRenderer.selectPlaceholder')}
        status={invalid ? 'error' : undefined}
        aria-invalid={invalid || undefined}
        style={{ width: '100%' }}
        options={(col.options ?? []).map((o) => ({
          value: o.value,
          label: optionLabel(o, language),
        }))}
        allowClear
      />
    );
  }
  if (col.type === 'RadioGroup') {
    return (
      <BmoRadioGroup
        value={typeof value === 'string' ? value : undefined}
        onChange={(v) => onChange(v ?? null)}
        disabled={disabled}
        ariaInvalid={invalid}
        options={(col.options ?? []).map((o) => ({
          value: o.value,
          label: optionLabel(o, language),
        }))}
      />
    );
  }
  // ShortText / LongText fall through. LongText uses TextArea; ShortText is a
  // regular Input. No mask — a column's only format rule is 'regex'.
  return (
    <TextCell
      col={col}
      value={typeof value === 'string' ? value : ''}
      onChange={(next) => onChange(applyInputRestriction(col.inputRestriction, next))}
      placeholder={placeholder}
      language={language}
      requiredMissing={invalid}
      disabled={disabled}
    />
  );
}

/** A cell holds an answer — a blank or whitespace-only string does not count. */
function isCellFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  return typeof value !== 'string' || value.trim() !== '';
}

/**
 * A blank required cell becomes an error only once Next has been pressed. Called from components
 * rendered INSIDE the row's FreshEntryErrorScope, which reports a tick of 0 for a row the advisor
 * added after a refused Next — so a fresh row stays clean until the following Next.
 *
 * Both the message and the cell's red border go through here, so the two can never disagree about
 * whether the cell is in error.
 */
function useRequiredCellError(requiredBlank: boolean): boolean {
  const showErrorsTick = useContext(ShowErrorsContext);
  return requiredBlank && showErrorsTick > 0;
}

/** The required message (the column's own wording, else "This field is required.") under a
 *  blank required cell, once Next has been pressed. */
function RequiredCellError({
  col,
  language,
  requiredBlank,
}: {
  col: RepeatableColumn;
  language: 'en' | 'fr';
  requiredBlank: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const show = useRequiredCellError(requiredBlank);
  return (
    <CellError
      id={id}
      message={
        show
          ? columnCellMessage(col, { errorKey: 'formRenderer.validation.required' }, language, t)
          : null
      }
    />
  );
}

/** The red text under a cell, wired to the control by `aria-describedby`. */
function CellError({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <div id={id} role="alert" style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>
      {message}
    </div>
  );
}

/**
 * A Number cell, with the column's authored `min` / `max` figures enforced beneath it (a fund's
 * minimum initial investment, a cap on an amount).
 *
 * The bounds are NOT handed to the input: AntD clamps a `min`/`max` to the bound when the advisor
 * leaves the cell, which on a dollar amount silently rewrites their money. The figure is kept and
 * named instead — once they leave the cell, or press Next like every other field — and FormShell's
 * `incompleteRepeatableColumns` holds Next on the same `columnCellViolation`, so the red text and
 * the held Next agree.
 */
function NumberCell({
  col,
  value,
  onChange,
  placeholder,
  unitMode,
  language,
  requiredMissing,
  disabled,
}: {
  col: RepeatableColumn;
  value: number | null;
  onChange: (next: number | null) => void;
  placeholder?: string;
  /** See {@link RepeatableCellInput}'s prop of the same name. */
  unitMode?: UnitMode;
  language: 'en' | 'fr';
  /** Blank + required + Next pressed, decided by the caller. The message for it
   *  is the sibling RequiredCellError, so this only reddens the border. */
  requiredMissing?: boolean;
  /** See {@link RepeatableRowControl}'s prop of the same name. */
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const errorId = useId();
  const [left, setLeft] = useState(false);
  const showErrorsTick = useContext(ShowErrorsContext);
  // The cell's unit comes from whichever of the two declarations the column carries. A
  // dual-unit column's mode is chosen by the ADVISOR, so it is read first; a column that
  // is always one unit carries an authored `numberFormat` instead — the scalar field's
  // property, under the scalar field's vocabulary. The BE refuses both on one column, so
  // this is a fallback rather than a precedence rule.
  //
  // Collapsing the two into one `unit` is what keeps the rules below from forking: a
  // percentage cell is bounded and capped the same way whoever decided it was a percentage.
  const unit: UnitMode | undefined =
    unitMode ??
    (col.numberFormat === 'currency'
      ? 'dollar'
      : col.numberFormat === 'percentage'
        ? 'percent'
        : undefined);
  const percent = unit === 'percent';
  const dollar = unit === 'dollar';
  // Negatives stay refused at the keyboard unless the column's own minimum says otherwise.
  const negatives = (col.min ?? 0) < 0;
  // How WIDE a figure this cell takes, from the column's authored Maximum — 999 admits any three
  // digits and refuses the fourth as it is typed. Never wider than the unit itself allows, so a
  // percentage cell stops at three digits even when the column authors no maximum of its own.
  // Distinct from the value rule below, which NAMES an out-of-range figure rather than clamping
  // it: a maximum of 123 still takes 800, and then says so. See integerDigitCap.
  const widthMax = percent ? Math.min(col.max ?? 100, 100) : col.max;
  const digitCap = integerDigitCap(percent ? 0 : col.min, widthMax);
  const violation = columnCellViolation(col, value);
  const error =
    (left || showErrorsTick > 0) && violation
      ? columnCellMessage(col, violation, language, t)
      : null;
  return (
    <>
      <InputNumber
        controls={false}
        disabled={disabled}
        value={value}
        onChange={(n) => {
          setLeft(false);
          onChange(n ?? null);
        }}
        onBlur={() => setLeft(true)}
        placeholder={placeholder}
        // A percentage is bounded 0–100 by the UNIT itself, so the input may clamp it: a
        // percentage over 100 is not a figure the advisor could have meant. An authored
        // min / max is a business rule and is reported rather than clamped — see above.
        min={percent ? 0 : undefined}
        max={percent ? 100 : undefined}
        prefix={dollar ? '$' : undefined}
        suffix={percent ? '%' : undefined}
        // A dollar cell is money, so it reads like money: the same thousands grouping the
        // scalar currency field has always had ("$ 1,250.75", not "$ 1250.75"). The two
        // controls are separate implementations and this one simply never had a formatter —
        // the `$` prefix arrived without it.
        //
        // Dollars only. A percentage is bounded 0–100 just above, so it has no grouping
        // position to reach, and the scalar percentage field carries no formatter either.
        // A Number column that declares no unit at all is left exactly as typed.
        formatter={dollar ? thousandsFormatter : undefined}
        // A blank required cell and an out-of-range one are one red border: the sibling
        // RequiredCellError owns the wording for the first case.
        status={error || requiredMissing ? 'error' : undefined}
        aria-invalid={error || requiredMissing ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        style={{ width: '100%' }}
        // This column had no parser at all, so it accepted anything typed. Same
        // guard as the scalar Number field — see numericInput.ts.
        // A repeatable column carries no integerOnly of its own, so decimals stay
        // allowed. Every Number column authored today is a dollar amount or a
        // percentage share: two decimals is the cap for both (QA D-41), and neither
        // can be negative (QA TFSA-D10 — a "-5" fund amount was accepted), so the
        // minus sign is refused as it is typed unless the column's own minimum is
        // below zero. Both are refused as they are typed.
        {...numericInputGuard({
          allowNegative: negatives,
          maxDecimals: 2,
          maxIntegerDigits: digitCap,
        })}
        // …and the same cap for a value set in one go (autofill / fill), which no key guard sees.
        // AntD's typing says the parser yields a number, but it decimal-parses a string too (the
        // scalar field relies on that) — and a string keeps a cleared cell blank rather than 0.
        parser={
          numericParser({ allowNegative: negatives, maxDecimals: 2 }) as unknown as (
            s?: string,
          ) => number
        }
      />
      <CellError id={errorId} message={error} />
    </>
  );
}

/**
 * A Date cell. On a `noPastDates` column the picker greys every day before today, and a past
 * date that is typed, or was stored before the rule existed, is named beneath the cell (QA
 * DP-021: the PAC Funds "Start date" took one). Same `columnCellViolation` FormShell's
 * `incompleteRepeatableColumns` holds Next on, so the red text and the held Next agree.
 *
 * AntD drops typed text its `disabledDate` refuses, leaving the cell blank with no word of why;
 * the text is still on the input at blur, so `validateTypedDate` reads it there — the Date
 * field's own blur check.
 */
function DateCell({
  col,
  value,
  onChange,
  placeholder,
  format,
  language,
  requiredMissing,
  disabled,
}: {
  col: RepeatableColumn;
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  format: WorkflowDateFormat;
  language: 'en' | 'fr';
  /** Blank + required + Next pressed, decided by the caller. The message for it
   *  is the sibling RequiredCellError, so this only reddens the border. */
  requiredMissing?: boolean;
  /** See {@link RepeatableRowControl}'s prop of the same name. */
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const errorId = useId();
  const [left, setLeft] = useState(false);
  const [typedError, setTypedError] = useState<string | null>(null);
  const showErrorsTick = useContext(ShowErrorsContext);
  const d = value ? dayjs(value) : null;
  const violation = columnCellViolation(col, value);
  const error =
    typedError ??
    ((left || showErrorsTick > 0) && violation
      ? columnCellMessage(col, violation, language, t)
      : null);
  const disabledDate = col.noPastDates
    ? (day: Dayjs) => columnCellViolation(col, day.format('YYYY-MM-DD')) !== null
    : undefined;
  return (
    <>
      {/* See dateInputClear.ts — antd's DatePicker discards a backspaced-to-empty
          edit on blur instead of committing it, so this wrapper commits it instead.
          The div itself isn't interactive — the DatePicker's <input> already owns
          focus/tab/keyboard — this is purely a bubble-listener relay. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyUp={clearOnEmptyBackspace(() => onChange(null))}>
        <BmoDatePicker
          value={d && d.isValid() ? d : null}
          onChange={(picked: Dayjs | null) => {
            setLeft(false);
            setTypedError(null);
            onChange(picked && picked.isValid() ? picked.format('YYYY-MM-DD') : null);
          }}
          onBlur={(e) => {
            setLeft(true);
            // Keep a typed date the picker refused, so Next holds on it — see parseTypedDate.
            const typedIso = parseTypedDate((e.target as HTMLInputElement).value, format);
            if (typedIso && typedIso !== value) onChange(typedIso);
            // A typed date the picker could not keep is named (not silently blanked), and a
            // past one on a noPastDates column is refused — the Date field's blur check.
            const typed = validateTypedDate(
              (e.target as HTMLInputElement).value,
              { noPastDates: !!col.noPastDates },
              {},
              format,
            );
            setTypedError(typed ? columnCellMessage(col, typed, language, t) : null);
          }}
          disabledDate={disabledDate}
          disabled={disabled}
          placeholder={placeholder}
          format={format}
          // A blank required cell and a rule-violating one are one red border:
          // the sibling RequiredCellError owns the wording for the first case.
          status={error || requiredMissing ? 'error' : undefined}
          aria-invalid={error || requiredMissing ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          style={{ width: '100%' }}
        />
      </div>
      <CellError id={errorId} message={error} />
    </>
  );
}

/**
 * A ShortText / LongText cell, with the column's rules enforced beneath it: its `regex` rule (QA
 * INV-018: "Wire order number" took `W  O`) and its min / max length.
 *
 * The DOM maxlength only stops keystrokes (a paste or a scripted fill walks past it) and there
 * was no minimum at all, so the RDSP "Fund number" took `1234`. The message appears once the
 * advisor leaves the cell — or presses Next, like every other field — and clears as they type,
 * matching the scalar fields. FormShell's `incompleteRepeatableColumns` holds Next on the same
 * `columnCellViolation`, so the red text and the held Next agree.
 */
function TextCell({
  col,
  value,
  onChange,
  placeholder,
  language,
  requiredMissing,
  disabled,
}: {
  col: RepeatableColumn;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  language: 'en' | 'fr';
  /** Blank + required + Next pressed, decided by the caller. The message for it
   *  is the sibling RequiredCellError, so this only reddens the border. */
  requiredMissing?: boolean;
  /** See {@link RepeatableRowControl}'s prop of the same name. */
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const errorId = useId();
  const [left, setLeft] = useState(false);
  const showErrorsTick = useContext(ShowErrorsContext);
  // ZeroBounce for an `email` column, on the same terms the scalar field gets it: asked once
  // per address when the advisor leaves the cell, null when this runtime may not verify.
  const emailVerify = useEmailVerify();
  const violation = columnCellViolation(col, value, undefined, emailVerify?.lookup);
  const error =
    (left || showErrorsTick > 0) && violation && !violation.silent
      ? columnCellMessage(col, violation, language, t)
      : null;
  const common = {
    value,
    placeholder,
    disabled,
    // TextCell only ever renders ShortText / LongText columns, and a column
    // applies no mask — so an uncapped one takes DEFAULT_MAX_LENGTH, the same
    // fallback the scalar fields take.
    maxLength: effectiveMaxLength(
      col.type === 'LongText' ? 'LongText' : 'ShortText',
      col.maxLength,
      col.minLength,
    ),
    // A blank required cell and a rule-violating one are one red border: the
    // sibling RequiredCellError owns the wording for the first case.
    status: error || requiredMissing ? ('error' as const) : undefined,
    'aria-invalid': error || requiredMissing ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    onBlur: () => {
      setLeft(true);
      // Idempotent per address — see useEmailVerification. A no-op for every column whose
      // rule isn't `email`, and for a value the syntax rule already rejects.
      if (col.validate === 'email') emailVerify?.request(value);
    },
  };
  return (
    <>
      {col.type === 'LongText' ? (
        <Input.TextArea
          {...common}
          onChange={(e) => {
            setLeft(false);
            onChange(e.target.value);
          }}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      ) : (
        <Input
          {...common}
          onChange={(e) => {
            setLeft(false);
            onChange(e.target.value);
          }}
        />
      )}
      <CellError id={errorId} message={error} />
    </>
  );
}

/** Per-column input-restriction. Same logic as the scalar ShortText/LongText
 *  renderer in FieldRenderer.tsx — kept inline so the RepeatableRowControl
 *  isn't pulled into FieldRenderer's circular import path. */
function applyInputRestriction(restriction: string | null | undefined, value: string): string {
  if (!restriction) return value;
  if (restriction === 'digits') return value.replace(/[^0-9]/g, '');
  if (restriction === 'letters') {
    const kept = value.replace(/[^\p{L}\p{M}'’\- ]/gu, '');
    return kept.replace(/(^|[^\p{L}\p{M}])(\p{M}+)/gu, '$1');
  }
  if (restriction === 'alphanumeric') return value.replace(/[^\p{L}\p{N} ]/gu, '');
  return value;
}

function optionLabel(o: SelectOption, language: 'en' | 'fr'): string {
  return (language === 'fr' ? o.labelFr : o.labelEn) || o.labelEn || o.labelFr;
}

/** Language-aware column help description, or '' when the column has no help
 *  authored in either language. */
function columnHelp(col: RepeatableColumn, language: 'en' | 'fr'): string {
  const active = language === 'fr' ? col.helpFr : col.helpEn;
  const fallback = language === 'fr' ? col.helpEn : col.helpFr;
  return (active?.trim() || fallback?.trim() || '') as string;
}

function emptyRow(template: RepeatableColumn[]): RepeatableRowValue {
  const row: RepeatableRowValue = {};
  for (const col of template) row[col.id] = null;
  return row;
}
